import {
    CardBreakpointRatios,
    createCardClickedEvent,
    FixedRatio,
    isPublicationCardItemWithVideo,
    PublicationCardItem,
    toLinkWithHint,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React from 'react'
import { CommonCardProps } from '../../cards/CardItem.Props'
import {
    getLandscapeWrapperTag,
    StyledCardHeadlineSkeleton,
    StyledHeader,
    StyledHeadline,
    StyledInlineWrapper,
    StyledLandscapeLink,
} from '../../cards/Landscape/Landscape.styled'
import { ResponsivePictureSizes } from '../../content/Picture/responsive'
import { MediaMode } from '../CardMedia/CardMedia'
import { ListCardTopicTag } from '../ListCard/components/ListCardTopicTag'
import { Bullet } from '../TheNightly/components/Bullet'
import {
    ByLine,
    TopicBylineWrapper,
} from '../TheNightly/LeadCard/LeadCard.styled'
import { LandscapeImageSize } from '../Landscape/Landscape'
import {
    StyledLandscapeNumber,
    StyledNightlyMedia,
} from './LandscapeNightly.styled'

export interface LandscapeNightlyProps extends CommonCardProps {
    className?: string
    cardNumber: number

    asListElement?: boolean
    hasBackground?: boolean
    hasPadding?: boolean
    hideByline?: boolean
    mediaMode?: MediaMode
    topicLevel?: 'parent'
    displayNumber?: boolean

    // Images
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    imageWidths?: ResponsivePictureSizes
    imagePosition?: 'left' | 'right'
    imageSize?: LandscapeImageSize
    item: MaybeLoaded<PublicationCardItem>
}

export const LandscapeNightly: React.FC<LandscapeNightlyProps> = (props) => {
    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!props.item.loaded) {
            e.preventDefault()
            return
        }
        props.onEvent(
            createCardClickedEvent(
                props.onEvent,
                props.item.result,
                'InternalLandscape',
                'Landscape',
                props.cardNumber,
            ),
        )
    }

    const cardItem = props.item

    const showByline = props.hideByline === false

    // Images
    const imageSize = props.imageSize || 'normal'
    const setImagePosition = props.imagePosition || 'left' // Default image position to left so it doesn't break existing cards
    const imageRatio = props.fixedRatio || '4:3'
    const imageRight = props.imagePosition === 'right'
    const imageLeft = props.imagePosition !== 'right'

    const StyledLandscape = getLandscapeWrapperTag(
        props.asListElement ? 'li' : 'div',
    )

    const fontScale = 1

    const defaultImageWidths = {
        mobile: '80px',
        tablet: '120px',
        desktop: '96px',
        fallbackWidth: 140,
        mode: 'fixed',
    } as ResponsivePictureSizes

    const renderImage = (
        <StyledNightlyMedia
            item={cardItem}
            imageWidths={
                props.imageWidths ? props.imageWidths : defaultImageWidths
            }
            fixedRatio={imageRatio}
            onEvent={props.onEvent}
            disableImageLazyLoad={props.disableImageLazyLoad}
            imagePosition={setImagePosition}
            imageSize={imageSize}
            mediaMode={props.mediaMode}
            CSSCrop={{ width: 1, height: 1 }}
            numbered={props.displayNumber ?? false}
        />
    )

    const renderHeadline = cardItem.loaded ? (
        <StyledHeadline
            headlineText={cardItem.result.shortHeadline}
            showVideoIcon={isPublicationCardItemWithVideo(cardItem.result)}
            kickerMode={'hidden'}
            publicationKind={cardItem.result.publicationKind}
            requiredAccess={cardItem.result.requiredAccess}
            fontScale={fontScale}
            imageSize={imageSize}
        />
    ) : (
        <StyledCardHeadlineSkeleton fontScale={fontScale} count={3} />
    )

    return (
        <StyledLandscape
            className={props.className}
            data-topic={cardItem.loaded && cardItem.result.primaryTopic.id}
            hasBackground={props.hasBackground || false}
            hasPadding={props.hasPadding}
        >
            <StyledLandscapeLink
                to={toLinkWithHint(cardItem)}
                onClick={linkClicked}
                isLoading={!cardItem.loaded}
                hasBackground={false}
            >
                {/* If not hiding, and we want image on the left! */}
                {props.mediaMode === 'hidden' ? null : imageLeft && renderImage}

                {props.displayNumber && (
                    <StyledLandscapeNumber>
                        {props.cardNumber}
                    </StyledLandscapeNumber>
                )}

                <StyledHeader>
                    {renderHeadline}
                    {cardItem.loaded && (
                        <StyledInlineWrapper>
                            <ListCardTopicTag
                                item={cardItem}
                                level={props.topicLevel}
                            />
                            {showByline && (
                                <TopicBylineWrapper>
                                    <ByLine>{cardItem.result.byline}</ByLine>
                                    <Bullet />
                                    <ByLine>
                                        {`${Math.ceil(
                                            cardItem.result.readTimeMinutes ??
                                                1,
                                        )} min read`}
                                    </ByLine>
                                </TopicBylineWrapper>
                            )}
                        </StyledInlineWrapper>
                    )}
                </StyledHeader>

                {/* If not hiding  and we want image on the right! */}
                {props.mediaMode === 'hidden'
                    ? null
                    : imageRight && renderImage}
            </StyledLandscapeLink>
        </StyledLandscape>
    )
}
LandscapeNightly.displayName = 'LandscapeNightly'
